import React, { useRef, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography
} from '@material-ui/core'
import { BasePage as CngBasePage, components } from 'cng-web-lib'
import { useForm, FormProvider } from 'react-hook-form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import MpciApiClient from '../Common/services/MpciApiClient'
import UaeMpciApiUrls from 'src/apiUrls/UaeMpciApiUrls'
import useBreadcrumbNameMap from 'src/useBreadcrumbNameMap'
import SnackBar from 'src/components/era-package/era/SnackBar'
import CngSection from 'src/components/cngcomponents/CngSection'
import MpciTable from '../Common/components/MpciTable'

const {
  button: { CngButton },
  form: {
    field: { CngTextField }
  },
  CngGridItem
} = components

// ─── Default form values ──────────────────────────────────────────────────────
const DEFAULT_VALUES = {
  module: '',
  configKey: '',
  configValue: ''
}

// ─── Table column definitions ─────────────────────────────────────────────────
const COLUMNS = [
  { field: 'module', title: 'Module', sortKey: 'module' },
  { field: 'configKey', title: 'Config Key', sortKey: 'configKey' },
  { field: 'configValue', title: 'Config Value' }
]

// ─── Table filter definitions ─────────────────────────────────────────────────
const FILTERS = [
  { name: 'module', label: 'Module', type: 'text' },
  { name: 'configKey', label: 'Config Key', type: 'text' }
]

// ─── View dialog (read-only) ────────────────────────────────────────────────────
function AppConfigViewDialog({ open, record, onClose, onEdit }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>App Config Details</Typography>
          <IconButton size='small' onClick={onClose}>
            <FontAwesomeIcon icon={['fal', 'times']} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant='caption' color='textSecondary'>Module</Typography>
            <Typography variant='body1'>{record?.module || '—'}</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant='caption' color='textSecondary'>Config Key</Typography>
            <Typography variant='body1'>{record?.configKey || '—'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='caption' color='textSecondary'>Config Value</Typography>
            <Typography variant='body1' style={{ wordBreak: 'break-all' }}>
              {record?.configValue || '—'}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <CngButton variant='outlined' onClick={onClose}>
          Close
        </CngButton>
        <CngButton
          color='primary'
          startIcon={<FontAwesomeIcon icon={['fal', 'pen']} />}
          onClick={() => { onClose(); onEdit(record) }}
        >
          Edit
        </CngButton>
      </DialogActions>
    </Dialog>
  )
}

// ─── Confirm delete dialog ────────────────────────────────────────────────────
function DeleteConfirmDialog({ open, record, onConfirm, onCancel, isDeleting }) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth='xs' fullWidth>
      <DialogTitle>Delete App Config</DialogTitle>
      <DialogContent>
        <Typography variant='body2'>
          Are you sure you want to delete the config entry{' '}
          <strong>
            [{record?.module}] {record?.configKey}
          </strong>
          ? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <CngButton variant='outlined' onClick={onCancel} disabled={isDeleting}>
          Cancel
        </CngButton>
        <CngButton
          color='secondary'
          onClick={onConfirm}
          disabled={isDeleting}
          shouldShowProgress={isDeleting}
        >
          Delete
        </CngButton>
      </DialogActions>
    </Dialog>
  )
}

// ─── Add / Edit form dialog ───────────────────────────────────────────────────
function AppConfigFormDialog({ open, mode, record, onClose, onSaved }) {
  const isEdit = mode === 'edit'
  const methods = useForm({ defaultValues: DEFAULT_VALUES })
  const [isSaving, setIsSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')

  // Populate form when editing
  React.useEffect(() => {
    if (open) {
      if (isEdit && record) {
        methods.reset({
          module: record.module ?? '',
          configKey: record.configKey ?? '',
          configValue: record.configValue ?? ''
        })
      } else {
        methods.reset(DEFAULT_VALUES)
      }
      setErrorMsg('')
    }
  }, [open, isEdit, record]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = methods.handleSubmit((formData) => {
    setIsSaving(true)
    setErrorMsg('')

    const payload = {
      module: formData.module,
      configKey: formData.configKey,
      configValue: formData.configValue
    }

    if (isEdit) {
      // PUT /mpci/app-config/update/{id}
      MpciApiClient.put(
        `${UaeMpciApiUrls.APP_CONFIG_UPDATE}/${record.id}`,
        payload,
        () => {
          setIsSaving(false)
          onSaved('Updated successfully.')
        },
        (err) => {
          setIsSaving(false)
          console.error('[AppConfig] update failed', err)
          setErrorMsg('Failed to update config. Please try again.')
        }
      )
    } else {
      // POST /mpci/app-config/create
      MpciApiClient.post(
        UaeMpciApiUrls.APP_CONFIG_CREATE,
        payload,
        () => {
          setIsSaving(false)
          onSaved('Created successfully.')
        },
        (err) => {
          setIsSaving(false)
          console.error('[AppConfig] create failed', err)
          setErrorMsg('Failed to create config. Please try again.')
        }
      )
    }
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography variant='h6'>
            {isEdit ? 'Edit App Config' : 'New App Config'}
          </Typography>
          <IconButton size='small' onClick={onClose}>
            <FontAwesomeIcon icon={['fal', 'times']} />
          </IconButton>
        </Box>
      </DialogTitle>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            {errorMsg && (
              <Box mb={2}>
                <Typography variant='body2' color='error'>
                  {errorMsg}
                </Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              <CngGridItem xs={12} sm={6}>
                <CngTextField
                  name='module'
                  label='Module *'
                  size='small'
                  inputProps={{ maxLength: 100 }}
                />
              </CngGridItem>

              <CngGridItem xs={12} sm={6}>
                <CngTextField
                  name='configKey'
                  label='Config Key *'
                  size='small'
                  inputProps={{ maxLength: 100 }}
                />
              </CngGridItem>

              <CngGridItem xs={12}>
                <CngTextField
                  name='configValue'
                  label='Config Value *'
                  size='small'
                  inputProps={{ maxLength: 500 }}
                />
              </CngGridItem>
            </Grid>
          </DialogContent>

          <DialogActions>
            <CngButton variant='outlined' onClick={onClose} disabled={isSaving}>
              Cancel
            </CngButton>
            <CngButton
              type='submit'
              color='primary'
              disabled={isSaving}
              shouldShowProgress={isSaving}
              startIcon={<FontAwesomeIcon icon={['fal', 'save']} />}
            >
              {isSaving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </CngButton>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}

// ─── Main content ─────────────────────────────────────────────────────────────
function AppConfigContent() {
  const tableRef = useRef(null)

  // Dialog states
  const [viewDialog, setViewDialog] = useState({ open: false, record: null })
  const [formDialog, setFormDialog] = useState({ open: false, mode: 'add', record: null })
  const [deleteDialog, setDeleteDialog] = useState({ open: false, record: null })
  const [isDeleting, setIsDeleting] = useState(false)

  // Snackbar states
  const [successSnack, setSuccessSnack] = useState({ open: false, message: '' })
  const [errorSnack, setErrorSnack] = useState({ open: false, message: '' })

  // ─── Handlers ──────────────────────────────────────────────────────────────
  function openView(row) {
    setViewDialog({ open: true, record: row })
  }

  function openAdd() {
    setFormDialog({ open: true, mode: 'add', record: null })
  }

  function openEdit(row) {
    setFormDialog({ open: true, mode: 'edit', record: row })
  }

  function openDelete(row) {
    setDeleteDialog({ open: true, record: row })
  }

  function handleFormSaved(message) {
    setFormDialog({ open: false, mode: 'add', record: null })
    setSuccessSnack({ open: true, message })
    if (tableRef.current) tableRef.current.performRefresh()
  }

  function handleDelete() {
    const { record } = deleteDialog
    if (!record) return

    setIsDeleting(true)

    // DELETE /mpci/app-config/delete/{id}
    MpciApiClient.del(
      `${UaeMpciApiUrls.APP_CONFIG_DELETE}/${record.id}`,
      {},
      () => {
        setIsDeleting(false)
        setDeleteDialog({ open: false, record: null })
        setSuccessSnack({ open: true, message: 'Deleted successfully.' })
        if (tableRef.current) tableRef.current.performRefresh()
      },
      (err) => {
        setIsDeleting(false)
        console.error('[AppConfig] delete failed', err)
        setDeleteDialog({ open: false, record: null })
        setErrorSnack({ open: true, message: 'Failed to delete config. Please try again.' })
      }
    )
  }

  // ─── Table action button ────────────────────────────────────────────────────
  const tableActions = [
    {
      component: () => (
        <CngButton
          color='primary'
          size='small'
          startIcon={<FontAwesomeIcon icon={['fal', 'plus']} />}
          onClick={openAdd}
        >
          New Config
        </CngButton>
      )
    }
  ]

  // ─── Row actions ───────────────────────────────────────────────────────────
  const rowActions = [
    {
      label: 'Edit',
      icon: <FontAwesomeIcon icon={['fal', 'pen']} />,
      onClick: (row) => openEdit(row)
    },
    {
      label: 'Delete',
      icon: <FontAwesomeIcon icon={['fal', 'trash']} />,
      onClick: (row) => openDelete(row)
    }
  ]

  return (
    <>
      {/* ── Snackbars ── */}
      <SnackBar
        title='Success'
        type='success'
        open={successSnack.open}
        onClose={() => setSuccessSnack({ open: false, message: '' })}
        duration={3000}
      >
        {successSnack.message}
      </SnackBar>

      <SnackBar
        title='Error'
        type='error'
        open={errorSnack.open}
        onClose={() => setErrorSnack({ open: false, message: '' })}
      >
        {errorSnack.message}
      </SnackBar>

      {/* ── Page header ── */}
      <Card>
        <CardContent>
          <CngSection
            title='App Configuration'
            icon={<FontAwesomeIcon icon={['fal', 'sliders-h']} />}
          >
            <Box mb={1}>
              <Typography variant='body2' color='textSecondary'>
                Manage application config entries. Each entry is identified by a module and a config key.
              </Typography>
            </Box>

            <MpciTable
              ref={tableRef}
              url={UaeMpciApiUrls.APP_CONFIG_LIST}
              columns={COLUMNS}
              filters={FILTERS}
              actions={tableActions}
              rowActions={rowActions}
              onRowClick={(row) => openView(row)}
              defaultSort={{ field: 'module', direction: 'ASC' }}
              flat
            />
          </CngSection>
        </CardContent>
      </Card>

      {/* ── View dialog ── */}
      <AppConfigViewDialog
        open={viewDialog.open}
        record={viewDialog.record}
        onClose={() => setViewDialog({ open: false, record: null })}
        onEdit={(row) => openEdit(row)}
      />

      {/* ── Add / Edit dialog ── */}
      <AppConfigFormDialog
        open={formDialog.open}
        mode={formDialog.mode}
        record={formDialog.record}
        onClose={() => setFormDialog({ open: false, mode: 'add', record: null })}
        onSaved={handleFormSaved}
      />

      {/* ── Delete confirm dialog ── */}
      <DeleteConfirmDialog
        open={deleteDialog.open}
        record={deleteDialog.record}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog({ open: false, record: null })}
        isDeleting={isDeleting}
      />
    </>
  )
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────
function AppConfigPage(props) {
  const breadcrumbNameMap = useBreadcrumbNameMap()
  return (
    <CngBasePage
      renderPage={() => <AppConfigContent {...props} />}
      extraCngBreadcrumbNameMap={breadcrumbNameMap}
    />
  )
}

export default AppConfigPage
